import React from 'react';
import {useSpring} from 'react-spring';

import debounce from 'lodash/debounce';
import noop from 'lodash/noop';

import {TLifeDuration} from './types';

export const useTimer = (lifeDuration: TLifeDuration, handleClose: TEmptyFunction) => {
  const isInfinityDuration = lifeDuration === 'infinity';

  const [lifeStyles, lifeStyleApi] = useSpring(() => ({
    percentage: isInfinityDuration ? 0 : 100,
    config: {duration: isInfinityDuration ? 0 : lifeDuration},
  }));

  const debouncedClose = React.useMemo(
    () => debounce(handleClose, isInfinityDuration ? 0 : (lifeDuration as number)),
    [handleClose, isInfinityDuration, lifeDuration],
  );

  const removeTimer = React.useCallback(() => {
    debouncedClose.cancel();

    // prevent console errors from uncaught error in react-spring-core
    try {
      lifeStyleApi.set({percentage: 100});
      lifeStyleApi.stop();
    } catch (e) {}
  }, [debouncedClose, lifeStyleApi]);

  const startTimer = React.useCallback(() => {
    debouncedClose();

    // prevent console errors from uncaught in react-spring-core
    try {
      lifeStyleApi.start({percentage: 0});
    } catch (e) {}
  }, [debouncedClose, lifeStyleApi]);

  React.useEffect(() => {
    if (!isInfinityDuration) {
      startTimer();

      return removeTimer;
    }

    return noop;
  }, [startTimer, removeTimer, isInfinityDuration]);

  return {
    lifeStyles,
    startTimer: isInfinityDuration ? noop : startTimer,
    removeTimer: isInfinityDuration ? noop : removeTimer,
  };
};
